import React, { useState, useEffect, useRef } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext.jsx';

import { ApiClient } from '../utils/apiClient.js';
import performanceMonitor from '../utils/performanceMonitor.js';
import browserCompatibility from '../utils/browserCompatibility.js';
import accessibilityUtils from '../utils/consolidatedAccessibility.js';
import debugLogger from '../utils/debugLogger.js';
import { reportInitializationError, reportApiError } from '../utils/consolidatedErrorHandling.js';
import developmentTools from '../utils/developmentTools.js';
import { useWizardSessionManager } from '../utils/wizardSessionManager.js';
import Step1ParrotConfig from '../components/Step1ParrotConfig.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import WizardProgressIndicator from '../components/WizardProgressIndicator.jsx';
import WizardBreadcrumbs from '../components/WizardBreadcrumbs.jsx';
import InitializationProgress from '../components/InitializationProgress.jsx';

// Import modular components for enhanced knowledge base functionality
import FileUploadModule from '../modules/file-upload/FileUploadModule.jsx';
import TextFileEditorModule from '../modules/text-editor/TextFileEditorModule.jsx';
import FileListModule from '../modules/file-list/FileListModule.jsx';
import FilterModule from '../modules/filter/FilterModule.jsx';
import { ModuleContextProvider } from '../modules/context/ModuleContextProvider.jsx';

/**
 * Parrot Wizard Step 1 Page
 * Handles basic parrot configuration including name, language, timing settings,
 * first message, and Quick Action template selection
 */
export default function ParrotWizardStep1() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For editing mode
  const { showToast } = useToast();
  const { getToken } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  
  // Enhanced timeout configuration for wizard data requests with browser compatibility
  const browserTimeoutAdjustment = browserCompatibility.getBrowserTimeoutAdjustment();
  const WIZARD_TIMEOUT = Math.round(45000 * browserTimeoutAdjustment); // Adjusted for browser
  const MAX_RETRY_ATTEMPTS = 3;
  
  // Request cancellation support
  const wizardDataRequestId = useRef(`wizard-data-${id || 'new'}-${Date.now()}`);
  
  // Configure API client with enhanced timeout for wizard requests
  useEffect(() => {
    // Initialize browser compatibility and accessibility
    const browserInfo = browserCompatibility.initializeBrowserCompatibility();
    accessibilityUtils.initializeAccessibility();
    
    // Set longer timeout for wizard data requests
    apiClient.defaultTimeout = WIZARD_TIMEOUT;
    
    // Enhanced logging with debug logger
    debugLogger.info('ParrotWizardStep1 component initialized', {
      parrotId: id,
      isEditing: !!id,
      browser: browserInfo.browser,
      timeout: WIZARD_TIMEOUT,
      adjustment: browserTimeoutAdjustment,
      hasNavigationState: !!location.state?.step1Data,
      hasSessionId: !!location.state?.sessionId
    }, { 
      component: 'ParrotWizardStep1',
      operation: 'component_initialization'
    });
    
    // Log browser compatibility details
    debugLogger.debug('Browser compatibility details', {
      browserInfo,
      supportedFeatures: browserInfo.support,
      timeoutAdjustment: browserTimeoutAdjustment
    }, {
      component: 'ParrotWizardStep1',
      category: 'browser_compatibility'
    });
    
    // Create debug panel in development mode
    if (debugLogger.isDebugMode) {
      debugLogger.createDebugPanel();
    }
    
    // Cleanup function to cancel requests when component unmounts
    return () => {
      debugLogger.info('ParrotWizardStep1 component unmounting', {
        parrotId: id,
        requestId: wizardDataRequestId.current,
        hasSessionManager: !!sessionManager
      }, {
        component: 'ParrotWizardStep1',
        operation: 'component_cleanup'
      });
      
      apiClient.cancelRequest(wizardDataRequestId.current);
      
      // Clean up session data if component unmounts without saving
      // Requirements: 1.6 - Clean up temporary session data on component unmount
      if (sessionManager && !location.state?.preserveSession) {
        try {
          // Only clear if we're not navigating to another wizard step
          const isNavigatingToWizardStep = window.location.pathname.includes('/step');
          if (!isNavigatingToWizardStep) {
            debugLogger.info('Cleaning up session data on unmount', {
              sessionId: sessionManager.sessionId,
              parrotId: id
            });
            // Note: We don't clear the session here as it might be needed for navigation
            // Session cleanup is handled by the session manager's garbage collection
          }
        } catch (error) {
          debugLogger.error('Error during session cleanup', error, {
            component: 'ParrotWizardStep1',
            operation: 'session_cleanup'
          });
        }
      }
    };
  }, [WIZARD_TIMEOUT, browserTimeoutAdjustment]);
  
  /**
   * Update initialization state with progress tracking and accessibility announcements
   */
  const updateInitializationState = (updates) => {
    setInitializationState(prev => {
      const newState = {
        ...prev,
        ...updates,
        lastAttemptTime: updates.status === 'retrying' ? Date.now() : prev.lastAttemptTime
      };
      
      // Log state changes for debugging
      debugLogger.logStateChange('ParrotWizardStep1', prev, newState, 'updateInitializationState');
      
      // Announce significant state changes to screen readers
      if (updates.status && updates.status !== prev.status) {
        const progress = updates.progress || prev.progress || 0;
        const step = updates.currentStep || prev.currentStep || '';
        accessibilityUtils.announceLoadingProgress(progress, step, updates.status);
        
        // Log accessibility announcement
        debugLogger.debug('Accessibility announcement made', {
          progress,
          step,
          status: updates.status
        }, {
          component: 'ParrotWizardStep1',
          category: 'accessibility'
        });
      }
      
      return newState;
    });
  };

  /**
   * Enhanced progress tracking with detailed steps and accessibility
   */
  const updateProgress = (step, progress, message) => {
    updateInitializationState({
      progress,
      currentStep: message,
      lastProgressUpdate: Date.now()
    });
    
    // Enhanced progress logging with debug logger
    debugLogger.logInitializationStep(step, 'progress', null, {
      progress,
      message,
      parrotId: id,
      timestamp: Date.now()
    });
    
    // Log performance metric for progress timing
    debugLogger.logPerformanceMetric('initialization_progress', progress, {
      unit: '%',
      step,
      message
    });
    
    // Announce progress milestones to screen readers
    if (progress % 25 === 0 || progress >= 100) {
      accessibilityUtils.announceLoadingProgress(progress, message, 'loading');
    }
  };
  
  /**
   * Check if an error is retryable based on error type and retry count
   */
  const isErrorRetryable = (error, retryCount) => {
    if (retryCount >= MAX_RETRY_ATTEMPTS) return false;
    
    // Network errors are retryable
    if (error.isNetworkError) return true;
    
    // Server errors (5xx) are retryable
    if (error.isServerError) return true;
    
    // Rate limiting is retryable
    if (error.status === 429) return true;
    
    // Enhanced API errors with canRetry flag
    if (error.canRetry) return true;
    
    return false;
  };
  
  /**
   * Get retry delay with exponential backoff
   */
  const getRetryDelay = (error, attempt) => {
    // Use retryAfter if provided by server
    if (error.retryAfter) {
      return error.retryAfter * 1000;
    }
    
    // Rate limiting - longer backoff
    if (error.status === 429) {
      return Math.min(2000 * Math.pow(2, attempt), 30000);
    }
    
    // Server errors - standard backoff
    if (error.isServerError) {
      return Math.min(2000 * Math.pow(2, attempt), 16000);
    }
    
    // Network errors - shorter backoff
    return Math.min(1000 * Math.pow(2, attempt), 8000);
  };
  
  /**
   * Categorize error for user display with enhanced error information
   */
  const categorizeError = (error) => {
    // Report error to error reporting system and get user-friendly error code
    const errorReport = reportInitializationError(error, 'error_categorization', null, {
      parrotId: id,
      hasNavigationState: !!location.state?.step1Data,
      hasSessionId: !!location.state?.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
    
    const errorCode = errorReport.errorCode;
    const userFriendlyMessage = errorReport.userFriendlyMessage;
    
    // Enhanced error logging
    debugLogger.error('Error categorized for user display', error, {
      component: 'ParrotWizardStep1',
      operation: 'error_categorization',
      errorCode,
      parrotId: id,
      category: 'error_handling'
    });
    
    if (error.isAuthError || error.status === 401 || error.status === 403) {
      return {
        type: 'authentication',
        message: error.status === 403 
          ? 'You don\'t have permission to access this parrot.' 
          : 'Your session has expired. Please sign in again.',
        code: errorCode,
        canRetry: false,
        action: 'redirect',
        details: 'Authentication credentials are invalid or expired',
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.isNetworkError || error.name === 'NetworkError' || error.message?.includes('fetch')) {
      return {
        type: 'network',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        code: errorCode,
        canRetry: true,
        action: 'retry',
        details: 'Network connectivity issue or DNS resolution failure',
        retryAfter: 3,
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.status === 404) {
      return {
        type: 'resource',
        message: id 
          ? 'The parrot you\'re trying to edit was not found or has been deleted.'
          : 'The requested resource was not found.',
        code: errorCode,
        canRetry: false,
        action: 'redirect',
        details: 'Resource does not exist or user lacks access permissions',
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.status === 429) {
      const retryAfter = error.retryAfter || 60;
      return {
        type: 'rate_limit',
        message: `Too many requests. Please wait ${retryAfter} seconds before trying again.`,
        code: errorCode,
        canRetry: true,
        action: 'retry',
        details: 'Rate limiting active due to high request volume',
        retryAfter,
        autoRetryIn: retryAfter,
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.isServerError || (error.status >= 500 && error.status < 600)) {
      return {
        type: 'server',
        message: 'Our servers are experiencing issues. Please try again in a few minutes.',
        code: errorCode,
        canRetry: true,
        action: 'retry',
        details: `Server error ${error.status}: ${error.statusText || 'Internal server error'}`,
        retryAfter: 5,
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      return {
        type: 'network',
        message: 'The request took too long to complete. This may be due to high server load.',
        code: errorCode,
        canRetry: true,
        action: 'retry',
        details: 'Request timeout - server did not respond within expected time',
        retryAfter: 10,
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    if (error.status >= 400 && error.status < 500) {
      return {
        type: 'validation',
        message: error.message || 'There was an issue with the request. Please check your input and try again.',
        code: errorCode,
        canRetry: false,
        action: 'retry',
        details: `Client error ${error.status}: ${error.statusText || 'Bad request'}`,
        supportMessage: userFriendlyMessage.supportMessage
      };
    }
    
    // Default error handling with more context
    return {
      type: 'general',
      message: error.message || 'An unexpected error occurred while loading the parrot data.',
      code: errorCode,
      canRetry: true,
      action: 'retry',
      details: `Unhandled error: ${error.name || 'Unknown'} - ${error.message || 'No details available'}`,
      retryAfter: 5,
      supportMessage: userFriendlyMessage.supportMessage
    };
  };
  
  const [formData, setFormData] = useState(() => {
    // Initialize with navigation state data if available
    if (location.state?.step1Data) {
      console.log('ParrotWizardStep1: Initializing with step1Data from navigation state:', location.state.step1Data);
      return location.state.step1Data;
    }
    return {};
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Initialize session manager for KB configuration persistence
  const sessionManager = useWizardSessionManager(
    location.state?.sessionId || formData.session_id,
    id, // parrotId for editing mode
    getToken
  );

  // Add session change listeners for KB configuration updates
  useEffect(() => {
    if (!sessionManager) return;

    const handleKbConfigChange = (event, data) => {
      debugLogger.debug('KB configuration changed in session', {
        event,
        hasConfig: !!data.config,
        files: data.config?.files?.length || 0,
        urls: data.config?.urls?.length || 0
      }, {
        component: 'ParrotWizardStep1',
        operation: 'kb_config_change'
      });

      // Update form data when KB configuration changes
      if (event === 'kb_config_updated' && data.config) {
        setFormData(prev => ({
          ...prev,
          knowledge_base: {
            url: data.config.urls?.[0]?.url || '',
            files: data.config.files || []
          },
          use_knowledge_base: prev.use_knowledge_base || 
            (data.config.files?.length > 0 || data.config.urls?.length > 0)
        }));
      }
    };

    const removeListener = sessionManager.addChangeListener(handleKbConfigChange);
    
    return () => {
      removeListener();
    };
  }, [sessionManager]);
  
  // Enhanced initialization state management
  const [initializationState, setInitializationState] = useState({
    status: 'initializing', // 'initializing', 'loading', 'success', 'error', 'retrying'
    progress: 0,
    currentStep: 'Preparing...',
    error: null,
    retryCount: 0,
    canRetry: true,
    startTime: Date.now(),
    lastAttemptTime: null
  });

  // Enhanced initialization with comprehensive error handling and retry logic
  useEffect(() => {
    const initializeData = async () => {
      const initStartTime = Date.now();
      
      // Enhanced initialization logging
      debugLogger.logInitializationStep('initialization', 'started', 0, {
        parrotId: id,
        isEditing: !!id,
        hasNavigationState: !!location.state?.step1Data,
        hasSessionId: !!location.state?.sessionId,
        timeout: WIZARD_TIMEOUT,
        userAgent: navigator.userAgent,
        url: window.location.href
      });
      
      // Record initialization start in performance monitor
      performanceMonitor.recordLoadTime('wizard_initialization_start', 0, {
        parrotId: id,
        isEditing: !!id,
        hasNavigationState: !!location.state?.step1Data,
        hasSessionId: !!location.state?.sessionId
      });
      
      updateInitializationState({
        status: 'initializing',
        progress: 0,
        currentStep: 'Preparing initialization...',
        error: null,
        retryCount: 0,
        canRetry: true,
        startTime: Date.now()
      });
      
      if (id) {
        setIsEditing(true);
      }

      try {
        // Step 1: Initial setup (10%)
        updateProgress('setup', 10, 'Analyzing request parameters...');
        await new Promise(resolve => setTimeout(resolve, 200)); // Brief pause for UX

        // Check if we have step1Data from navigation state (coming back from step 2)
        if (location.state?.step1Data && Object.keys(formData).length === 0) {
          debugLogger.info('Loading step1Data from navigation state', {
            dataKeys: Object.keys(location.state.step1Data),
            parrotId: id
          }, {
            component: 'ParrotWizardStep1',
            operation: 'navigation_state_load'
          });
          
          updateProgress('navigation', 30, 'Loading navigation data...');
          await new Promise(resolve => setTimeout(resolve, 300));
          
          updateProgress('processing', 70, 'Processing navigation data...');
          setFormData(location.state.step1Data);
          await new Promise(resolve => setTimeout(resolve, 200));
          
          updateProgress('complete', 100, 'Ready');
          updateInitializationState({ status: 'success' });
          
          // Record successful navigation state load
          const totalTime = Date.now() - initStartTime;
          performanceMonitor.recordLoadTime('wizard_initialization_navigation_state', totalTime, {
            parrotId: id,
            dataSource: 'navigation_state'
          });
          
          debugLogger.logInitializationStep('navigation_state_load', 'completed', totalTime, {
            parrotId: id,
            dataSource: 'navigation_state'
          });
        } else if (location.state?.sessionId && Object.keys(formData).length === 0) {
          // Load from wizard session if we have a session ID
          debugLogger.info('Loading from wizard session', {
            sessionId: location.state.sessionId,
            parrotId: id
          }, {
            component: 'ParrotWizardStep1',
            operation: 'session_load'
          });
          
          updateProgress('session', 20, 'Connecting to wizard session...');
          await loadWizardSessionDataWithRetry(location.state.sessionId);
          
          // Record successful session load
          const totalTime = Date.now() - initStartTime;
          performanceMonitor.recordLoadTime('wizard_initialization_session', totalTime, {
            parrotId: id,
            sessionId: location.state.sessionId,
            dataSource: 'wizard_session'
          });
          
          debugLogger.logInitializationStep('session_load', 'completed', totalTime, {
            parrotId: id,
            sessionId: location.state.sessionId,
            dataSource: 'wizard_session'
          });
        } else if (id && Object.keys(formData).length === 0) {
          // Only load from API if we don't have navigation state data
          debugLogger.info('Loading from API for parrot editing', {
            parrotId: id,
            timeout: WIZARD_TIMEOUT
          }, {
            component: 'ParrotWizardStep1',
            operation: 'api_load'
          });
          
          updateProgress('validation', 20, 'Validating parrot access...');
          await loadExistingParrotDataWithRetry();
          
          // Record successful API load
          const totalTime = Date.now() - initStartTime;
          performanceMonitor.recordLoadTime('wizard_initialization_api', totalTime, {
            parrotId: id,
            dataSource: 'api_wizard_data'
          });
          
          debugLogger.logInitializationStep('api_load', 'completed', totalTime, {
            parrotId: id,
            dataSource: 'api_wizard_data'
          });
        } else if (Object.keys(formData).length === 0) {
          // For new parrots without navigation state, initialize with defaults
          debugLogger.info('Initializing new parrot with defaults', {
            parrotId: id
          }, {
            component: 'ParrotWizardStep1',
            operation: 'default_initialization'
          });
          
          updateProgress('defaults', 30, 'Setting up new parrot form...');
          await new Promise(resolve => setTimeout(resolve, 300));
          
          updateProgress('form', 70, 'Initializing form fields...');
          setFormData({
            name: '',
            language: 'en',
            max_call_length_seconds: '',
            turn_timeout_seconds: 7,
            max_silence_seconds: 20,
            first_message: '',
            selected_template_id: null,
            use_rag: false,
            knowledge_base_urls: [],
            selected_rag_files: [],
            knowledge_base: { url: '', files: [] }
          });
          await new Promise(resolve => setTimeout(resolve, 200));
          
          updateProgress('complete', 100, 'Ready');
          updateInitializationState({ status: 'success' });
          
          // Record successful default initialization
          const totalTime = Date.now() - initStartTime;
          performanceMonitor.recordLoadTime('wizard_initialization_defaults', totalTime, {
            parrotId: id,
            dataSource: 'defaults'
          });
          
          debugLogger.logInitializationStep('default_initialization', 'completed', totalTime, {
            parrotId: id,
            dataSource: 'defaults'
          });
        } else {
          // Form data already exists, mark as ready
          debugLogger.info('Form data already loaded, skipping initialization', {
            parrotId: id,
            formDataKeys: Object.keys(formData)
          }, {
            component: 'ParrotWizardStep1',
            operation: 'existing_data'
          });
          
          updateProgress('existing', 50, 'Form data already loaded...');
          await new Promise(resolve => setTimeout(resolve, 200));
          
          updateProgress('complete', 100, 'Ready');
          updateInitializationState({ status: 'success' });
          
          // Record existing data load
          const totalTime = Date.now() - initStartTime;
          performanceMonitor.recordLoadTime('wizard_initialization_existing', totalTime, {
            parrotId: id,
            dataSource: 'existing_form_data'
          });
          
          debugLogger.logInitializationStep('existing_data', 'completed', totalTime, {
            parrotId: id,
            dataSource: 'existing_form_data'
          });
        }
      } catch (error) {
        const totalTime = Date.now() - initStartTime;
        
        // Enhanced error logging and reporting
        debugLogger.logInitializationStep('initialization', 'failed', totalTime, {
          error,
          parrotId: id,
          hasNavigationState: !!location.state?.step1Data,
          hasSessionId: !!location.state?.sessionId
        });
        
        const categorizedError = categorizeError(error);
        updateInitializationState({
          status: 'error',
          error: {
            ...categorizedError,
            originalError: error,
            timestamp: new Date().toISOString()
          },
          canRetry: categorizedError.canRetry
        });
        
        // Record initialization failure
        performanceMonitor.recordLoadTime('wizard_initialization_error', totalTime, {
          parrotId: id,
          error: error.message,
          errorType: error.name,
          dataSource: 'error'
        });
        
        // Log user action for debugging user flows
        debugLogger.logUserAction('initialization_failed', null, {
          parrotId: id,
          errorCode: categorizedError.code,
          errorType: categorizedError.type,
          canRetry: categorizedError.canRetry
        });
      }
    };

    initializeData();
  }, [id, location.state]);

  /**
   * Load wizard session data with retry logic using session manager
   * Requirements: 1.1, 1.2, 1.3 - Session-based KB configuration persistence
   */
  const loadWizardSessionDataWithRetry = async (sessionId, retryCount = 0) => {
    try {
      updateInitializationState({
        status: retryCount > 0 ? 'retrying' : 'loading',
        retryCount
      });
      
      if (retryCount > 0) {
        updateProgress('retry', 30, `Retrying session load (${retryCount}/${MAX_RETRY_ATTEMPTS})...`);
      } else {
        updateProgress('session', 30, 'Connecting to wizard session...');
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));
      updateProgress('request', 50, 'Requesting session data...');
      
      // Enhanced session loading for editing mode
      // Requirements: 12.1, 12.2, 12.3 - Pre-populate existing knowledge base data
      sessionManager.sessionId = sessionId;
      
      let backendConfig = null;
      if (id) {
        // For editing mode, load existing parrot data
        updateProgress('load_existing', 60, 'Loading existing parrot configuration...');
        backendConfig = await sessionManager.loadExistingParrotData();
        
        if (backendConfig) {
          console.log('Loaded existing parrot data for editing:', {
            parrotId: id,
            hasStep1: !!backendConfig.step1,
            hasKnowledgeBase: !!(backendConfig.step1?.knowledge_base),
            kbFilesCount: backendConfig.step1?.knowledge_base?.files?.length || 0,
            kbUrl: backendConfig.step1?.knowledge_base?.url
          });
        }
      } else {
        // For new parrots, load from session
        backendConfig = await sessionManager.loadFromBackend();
      }
      
      updateProgress('validate', 70, 'Validating session data...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get wizard state from session manager (now includes existing parrot data)
      const wizardState = sessionManager.getState();
      const step1Data = wizardState?.step1Data || {};
      
      updateProgress('process', 85, 'Processing session data...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Map session data to form data structure with KB configuration
      const mappedData = {
        name: step1Data.name || '',
        language: step1Data.language || 'en',
        max_call_length_seconds: step1Data.max_call_length_seconds || '',
        turn_timeout_seconds: step1Data.turn_timeout_seconds || 7,
        max_silence_seconds: step1Data.max_silence_seconds || 20,
        first_message: step1Data.first_message || '',
        selected_template_id: step1Data.selected_template_id || null,
        use_rag: step1Data.use_rag || false,
        use_knowledge_base: step1Data.use_knowledge_base || false,
        knowledge_base_urls: step1Data.knowledge_base_urls || [],
        selected_rag_files: step1Data.selected_rag_files || [],
        knowledge_base: step1Data.knowledge_base || { url: '', files: [] },
        session_id: sessionId
      };
      
      // Enhanced KB configuration loading with pre-population support
      // Requirements: 12.1, 12.2, 12.3 - Pre-populate existing knowledge base data
      const kbConfig = sessionManager.getKnowledgeBaseConfig();
      if (kbConfig && (kbConfig.files.length > 0 || kbConfig.urls.length > 0 || kbConfig.textContent.length > 0)) {
        mappedData.knowledge_base = {
          url: kbConfig.urls[0]?.url || '',
          files: kbConfig.files.map(file => ({
            ...file,
            // Ensure proper mapping for pre-populated files
            name: file.name,
            selected: file.selected !== undefined ? file.selected : true,
            existing: file.existing || false
          })),
          text_documents: kbConfig.textContent || []
        };
        
        // Auto-enable knowledge base if we have content
        mappedData.use_knowledge_base = true;
        
        console.log('Pre-populated KB configuration from session:', {
          filesCount: kbConfig.files.length,
          urlsCount: kbConfig.urls.length,
          textContentCount: kbConfig.textContent.length,
          hasExistingContent: kbConfig.metadata?.loaded_from_existing || false
        });
      }
      
      console.log('ParrotWizardStep1: Mapped session data with KB config:', mappedData);
      setFormData(mappedData);
      
      updateProgress('complete', 100, 'Ready');
      updateInitializationState({ status: 'success' });
      
      debugLogger.info('Session data loaded successfully with KB configuration', {
        sessionId,
        hasKbConfig: !!kbConfig,
        kbFiles: kbConfig?.files?.length || 0,
        kbUrls: kbConfig?.urls?.length || 0
      }, {
        component: 'ParrotWizardStep1',
        operation: 'session_load_success'
      });
      
    } catch (error) {
      console.error('Error loading wizard session data:', error);
      
      const categorizedError = categorizeError(error);
      
      if (isErrorRetryable(error, retryCount)) {
        const delay = getRetryDelay(error, retryCount);
        
        updateInitializationState({
          status: 'retrying',
          currentStep: `Retrying in ${Math.ceil(delay / 1000)} seconds...`,
          retryCount: retryCount + 1
        });
        
        setTimeout(() => {
          loadWizardSessionDataWithRetry(sessionId, retryCount + 1);
        }, delay);
      } else {
        // Fallback to defaults if all retries exhausted
        console.log('ParrotWizardStep1: Session load failed, falling back to defaults');
        updateProgress('fallback', 70, 'Loading default settings...');
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const defaultData = {
          name: '',
          language: 'en',
          max_call_length_seconds: '',
          turn_timeout_seconds: 7,
          max_silence_seconds: 20,
          first_message: '',
          selected_template_id: null,
          use_rag: false,
          use_knowledge_base: false,
          knowledge_base_urls: [],
          selected_rag_files: [],
          knowledge_base: { url: '', files: [] }
        };
        
        setFormData(defaultData);
        
        // Initialize session manager with default data
        sessionManager.saveState(1, defaultData, { completed: false });
        
        updateInitializationState({
          status: 'error',
          error: {
            ...categorizedError,
            originalError: error,
            timestamp: new Date().toISOString(),
            fallbackUsed: true
          },
          canRetry: false
        });
        
        showToast('Failed to load session data, using defaults', 'warning');
      }
    }
  };

  /**
   * Load existing parrot data with comprehensive retry logic
   */
  const loadExistingParrotDataWithRetry = async (retryCount = 0) => {
    try {
      updateInitializationState({
        status: retryCount > 0 ? 'retrying' : 'loading',
        retryCount
      });
      
      if (retryCount > 0) {
        updateProgress('retry', 30, `Retrying data load (${retryCount}/${MAX_RETRY_ATTEMPTS})...`);
      } else {
        updateProgress('validation', 30, 'Validating parrot access...');
      }
      
      // Clear any potential cached data that might contain stale file references
      try {
        localStorage.removeItem(`parrot_${id}_wizard_data`);
        sessionStorage.removeItem(`parrot_${id}_wizard_data`);
      } catch (e) {
        // Ignore storage errors
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));
      updateProgress('request', 50, 'Requesting parrot configuration...');
      
      // Log API request start
      const apiRequestStartTime = Date.now();
      debugLogger.logApiRequest(
        wizardDataRequestId.current,
        'GET',
        `/api/parrots/${id}/wizard-data`,
        {
          timeout: WIZARD_TIMEOUT,
          retryCount,
          bustCache: retryCount > 0 ? 'strong' : true
        }
      );
      
      // Fetch the existing parrot data from the API with cache-busting
      const response = await apiClient.cancellableGet(
        `/api/parrots/${id}/wizard-data`, 
        {
          timeout: WIZARD_TIMEOUT,
          bustCache: retryCount > 0 ? 'strong' : true, // Use strong cache busting on retries
          cacheVersion: `retry-${retryCount}`,
          deduplication: retryCount === 0, // Only deduplicate first attempt
          userId: await apiClient.getToken().then(token => apiClient.extractUserIdFromToken(token))
        },
        wizardDataRequestId.current
      );
      
      // Log API response
      const apiRequestDuration = Date.now() - apiRequestStartTime;
      debugLogger.logApiResponse(
        wizardDataRequestId.current,
        200,
        apiRequestDuration,
        {
          dataSize: JSON.stringify(response).length,
          cacheHit: false,
          retryCount
        }
      );
      
      debugLogger.info('API Response received successfully', {
        parrotId: id,
        responseSize: JSON.stringify(response).length,
        duration: apiRequestDuration,
        retryCount
      }, {
        component: 'ParrotWizardStep1',
        operation: 'api_response',
        requestId: wizardDataRequestId.current
      });
      
      updateProgress('validate', 70, 'Validating configuration data...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateProgress('process', 85, 'Processing configuration data...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Map the API response to form data structure
      const step1Data = response.wizard_data?.step1 || {};
      console.log('ParrotWizardStep1: Step1 Data:', step1Data);
      
      // Debug: Check knowledge base files from API response
      const knowledgeBaseFiles = step1Data.knowledge_base?.files || step1Data.selected_rag_files || [];
      if (knowledgeBaseFiles && Array.isArray(knowledgeBaseFiles)) {
        console.log('ParrotWizardStep1: Knowledge Base files from API:', knowledgeBaseFiles.map(f => ({ id: f.id, name: f.original_filename || f.name })));
        const problematicFiles = knowledgeBaseFiles.filter(f => parseInt(f.id) > 10);
        if (problematicFiles.length > 0) {
          console.error('ParrotWizardStep1: FOUND PROBLEMATIC FILES FROM API:', problematicFiles);
        }
      }
      
      const existingData = {
        parrot_id: id, // Add parrot ID for validation
        name: step1Data.name || '',
        language: step1Data.language || 'en', // Ensure language defaults to 'en'
        max_call_length_seconds: step1Data.max_call_length_seconds || '',
        turn_timeout_seconds: step1Data.turn_timeout_seconds || 7,
        max_silence_seconds: step1Data.max_silence_seconds || 20,
        first_message: step1Data.first_message || '',
        selected_template_id: step1Data.selected_template_id || null,
        use_knowledge_base: step1Data.use_knowledge_base, // Extract use_knowledge_base from API response
        use_rag: step1Data.use_rag || false,
        // Legacy support
        knowledge_base_urls: step1Data.knowledge_base_urls || [],
        selected_rag_files: step1Data.selected_rag_files || [],
        // New knowledge base structure
        knowledge_base: step1Data.knowledge_base || {
          url: step1Data.knowledge_base_urls?.[0] || '',
          files: knowledgeBaseFiles.map(file => ({
            id: file.id,
            name: file.original_filename || file.name,
            size: file.file_size || file.size,
            type: file.type || 'file',
            mime_type: file.mime_type,
            r2_key: file.r2_key || file.file_path,
            storage_name: file.storage_name,
            sync_status: file.sync_status || 'synced',
            created_at: file.created_at,
            updated_at: file.updated_at,
            uploadedAt: file.created_at
          }))
        }
      };
      
      console.log('ParrotWizardStep1: Setting existing data:', existingData);
      setFormData(existingData);
      
      updateProgress('complete', 100, 'Ready');
      updateInitializationState({ status: 'success' });
    } catch (error) {
      // Enhanced error logging and reporting
      debugLogger.error('Failed to load parrot data', error, {
        component: 'ParrotWizardStep1',
        operation: 'loadExistingParrotDataWithRetry',
        parrotId: id,
        retryCount,
        requestId: wizardDataRequestId.current
      });
      
      // Report API error
      reportApiError(
        error,
        wizardDataRequestId.current,
        `/api/parrots/${id}/wizard-data`,
        'GET',
        Date.now() - (performance.now() - 1000) // Approximate duration
      );
      
      const categorizedError = categorizeError(error);
      
      if (isErrorRetryable(error, retryCount)) {
        const delay = getRetryDelay(error, retryCount);
        
        debugLogger.warn('Retrying parrot data load', {
          retryCount: retryCount + 1,
          delay,
          errorType: error.name,
          errorMessage: error.message
        }, {
          component: 'ParrotWizardStep1',
          operation: 'retry_logic',
          parrotId: id
        });
        
        updateInitializationState({
          status: 'retrying',
          currentStep: `Retrying in ${Math.ceil(delay / 1000)} seconds...`,
          retryCount: retryCount + 1
        });
        
        setTimeout(() => {
          loadExistingParrotDataWithRetry(retryCount + 1);
        }, delay);
      } else {
        debugLogger.error('Parrot data load failed permanently', error, {
          component: 'ParrotWizardStep1',
          operation: 'permanent_failure',
          parrotId: id,
          retryCount,
          maxRetries: MAX_RETRY_ATTEMPTS,
          errorCode: categorizedError.code
        });
        
        updateInitializationState({
          status: 'error',
          error: {
            ...categorizedError,
            originalError: error,
            timestamp: new Date().toISOString()
          },
          canRetry: categorizedError.canRetry && retryCount < MAX_RETRY_ATTEMPTS
        });
        
        // For critical errors like 404 or auth errors, redirect
        if (categorizedError.action === 'redirect') {
          debugLogger.info('Redirecting due to critical error', {
            errorType: categorizedError.type,
            errorCode: categorizedError.code,
            redirectTo: '/parrots'
          }, {
            component: 'ParrotWizardStep1',
            operation: 'error_redirect'
          });
          
          showToast(categorizedError.message, 'error');
          setTimeout(() => navigate('/parrots'), 2000);
        }
      }
    }
  };

  /**
   * Handle session recovery from failures
   * Requirements: 6.5, 6.6 - Error recovery mechanisms for session failures
   */
  const handleSessionRecovery = async () => {
    try {
      debugLogger.info('Attempting session recovery', {
        sessionId: sessionManager?.sessionId,
        parrotId: id
      }, {
        component: 'ParrotWizardStep1',
        operation: 'session_recovery'
      });

      updateInitializationState({
        status: 'loading',
        progress: 0,
        currentStep: 'Recovering session...',
        error: null
      });

      if (sessionManager) {
        // Try to validate and recover session state
        const validation = sessionManager.validateSession();
        
        if (!validation.valid) {
          debugLogger.warn('Session validation failed, attempting recovery', {
            issues: validation.issues,
            sessionId: sessionManager.sessionId
          });
          
          // Clear corrupted session data
          sessionManager.clearState();
          
          // Reinitialize with defaults
          const defaultData = {
            name: '',
            language: 'en',
            max_call_length_seconds: '',
            turn_timeout_seconds: 7,
            max_silence_seconds: 20,
            first_message: '',
            selected_template_id: null,
            use_knowledge_base: false,
            knowledge_base_urls: [],
            selected_rag_files: [],
            knowledge_base: { url: '', files: [] }
          };
          
          sessionManager.saveState(1, defaultData, { completed: false });
          setFormData(defaultData);
          
          updateProgress('recovery', 100, 'Session recovered with defaults');
          updateInitializationState({ status: 'success' });
          
          showToast('Session recovered. Some data may have been lost.', 'warning');
          
          debugLogger.info('Session recovery completed', {
            sessionId: sessionManager.sessionId,
            recoveryMethod: 'defaults'
          });
          
          return true;
        } else {
          // Session is valid, try to reload from it
          const state = sessionManager.getState();
          if (state && state.step1Data) {
            setFormData(state.step1Data);
            updateProgress('recovery', 100, 'Session recovered from local storage');
            updateInitializationState({ status: 'success' });
            
            debugLogger.info('Session recovery completed', {
              sessionId: sessionManager.sessionId,
              recoveryMethod: 'local_storage'
            });
            
            return true;
          }
        }
      }
      
      // If session manager recovery fails, fall back to default handling
      return false;
      
    } catch (error) {
      debugLogger.error('Session recovery failed', error, {
        component: 'ParrotWizardStep1',
        operation: 'session_recovery_error'
      });
      
      return false;
    }
  };

  /**
   * Handle fallback to default settings when errors are not recoverable
   */
  const handleFallback = async () => {
    console.log('ParrotWizardStep1: Using fallback defaults due to unrecoverable error');
    
    // First try session recovery
    const sessionRecovered = await handleSessionRecovery();
    if (sessionRecovered) {
      return;
    }
    
    updateInitializationState({
      status: 'loading',
      progress: 0,
      currentStep: 'Loading default settings...',
      error: null
    });
    
    try {
      updateProgress('fallback', 30, 'Preparing default configuration...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateProgress('defaults', 70, 'Setting up form with defaults...');
      
      const defaultData = {
        name: id ? `Parrot ${id}` : '',
        language: 'en',
        max_call_length_seconds: '',
        turn_timeout_seconds: 7,
        max_silence_seconds: 20,
        first_message: '',
        selected_template_id: null,
        use_rag: false,
        use_knowledge_base: false,
        knowledge_base_urls: [],
        selected_rag_files: [],
        knowledge_base: { url: '', files: [] }
      };
      
      setFormData(defaultData);
      
      // Initialize session manager with default data
      if (sessionManager) {
        sessionManager.saveState(1, defaultData, { completed: false });
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateProgress('complete', 100, 'Ready with default settings');
      updateInitializationState({ 
        status: 'success',
        error: {
          fallbackUsed: true,
          message: 'Using default settings due to loading error'
        }
      });
      
      showToast('Loaded with default settings. Some data may be missing.', 'warning');
      
    } catch (fallbackError) {
      console.error('Fallback failed:', fallbackError);
      updateInitializationState({
        status: 'error',
        error: {
          type: 'general',
          message: 'Unable to load even default settings. Please refresh the page.',
          canRetry: true,
          originalError: fallbackError
        }
      });
    }
  };

  /**
   * Manual retry function for user-initiated retries
   */
  const handleRetry = async () => {
    const retryStartTime = Date.now();
    
    // Log user action and retry initiation
    debugLogger.logUserAction('manual_retry_initiated', null, {
      parrotId: id,
      hasSessionId: !!location.state?.sessionId,
      previousError: initializationState.error?.code
    });
    
    debugLogger.info('Manual retry initiated by user', {
      parrotId: id,
      previousError: initializationState.error,
      hasSessionId: !!location.state?.sessionId
    }, {
      component: 'ParrotWizardStep1',
      operation: 'manual_retry'
    });
    
    updateInitializationState({
      status: 'loading',
      progress: 0,
      currentStep: 'Preparing retry...',
      error: null,
      retryCount: 0,
      canRetry: true,
      startTime: Date.now()
    });
    
    try {
      updateProgress('retry', 10, 'Initializing retry process...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // First try session recovery if we have a session manager
      if (sessionManager) {
        updateProgress('session_recovery', 20, 'Attempting session recovery...');
        const sessionRecovered = await handleSessionRecovery();
        if (sessionRecovered) {
          return; // Session recovery successful
        }
      }
      
      if (location.state?.sessionId) {
        debugLogger.info('Manual retry: Loading from session', {
          sessionId: location.state.sessionId
        }, {
          component: 'ParrotWizardStep1',
          operation: 'manual_retry_session'
        });
        await loadWizardSessionDataWithRetry(location.state.sessionId);
      } else if (id) {
        debugLogger.info('Manual retry: Loading from API', {
          parrotId: id
        }, {
          component: 'ParrotWizardStep1',
          operation: 'manual_retry_api'
        });
        await loadExistingParrotDataWithRetry();
      } else {
        // For new parrots, just initialize with defaults
        debugLogger.info('Manual retry: Using defaults for new parrot', {}, {
          component: 'ParrotWizardStep1',
          operation: 'manual_retry_defaults'
        });
        
        updateProgress('defaults', 50, 'Loading default settings...');
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const defaultData = {
          name: '',
          language: 'en',
          max_call_length_seconds: '',
          turn_timeout_seconds: 7,
          max_silence_seconds: 20,
          first_message: '',
          selected_template_id: null,
          use_rag: false,
          use_knowledge_base: false,
          knowledge_base_urls: [],
          selected_rag_files: [],
          knowledge_base: { url: '', files: [] }
        };
        
        setFormData(defaultData);
        
        // Initialize session manager with default data
        if (sessionManager) {
          sessionManager.saveState(1, defaultData, { completed: false });
        }
        
        updateProgress('complete', 100, 'Ready');
        updateInitializationState({ status: 'success' });
      }
      
      // Log successful retry
      const retryDuration = Date.now() - retryStartTime;
      debugLogger.info('Manual retry completed successfully', {
        duration: retryDuration,
        parrotId: id
      }, {
        component: 'ParrotWizardStep1',
        operation: 'manual_retry_success'
      });
      
      debugLogger.logPerformanceMetric('manual_retry_duration', retryDuration, {
        unit: 'ms',
        threshold: 5000,
        parrotId: id
      });
      
    } catch (error) {
      const retryDuration = Date.now() - retryStartTime;
      
      debugLogger.error('Manual retry failed', error, {
        component: 'ParrotWizardStep1',
        operation: 'manual_retry_failed',
        duration: retryDuration,
        parrotId: id
      });
      
      const categorizedError = categorizeError(error);
      updateInitializationState({
        status: 'error',
        error: {
          ...categorizedError,
          originalError: error,
          timestamp: new Date().toISOString(),
          isManualRetry: true
        },
        canRetry: categorizedError.canRetry
      });
      
      // Log user action for failed retry
      debugLogger.logUserAction('manual_retry_failed', null, {
        parrotId: id,
        errorCode: categorizedError.code,
        errorType: categorizedError.type,
        duration: retryDuration
      });
    }
  };

  /**
   * Handle form data changes
   */
  const handleFormDataChange = (newData) => {
    console.log('ParrotWizardStep1: Form data changed:', newData);
    setFormData(newData);
    // Clear errors when data changes
    setErrors({});
  };

  /**
   * Handle proceeding to Step 2 with session manager integration
   * Requirements: 1.1, 1.2, 1.3 - KB configuration state preservation during navigation
   */
  const handleNext = async (stepData) => {
    try {
      setIsLoading(true);
      setErrors({});

      console.log('ParrotWizardStep1: handleNext received stepData:', stepData);

      debugLogger.info('Proceeding to Step 2 with session manager', {
        hasSessionManager: !!sessionManager,
        sessionId: sessionManager?.sessionId,
        parrotId: id,
        hasKbConfig: !!(stepData.knowledge_base && (stepData.knowledge_base.files?.length > 0 || stepData.knowledge_base.url))
      }, {
        component: 'ParrotWizardStep1',
        operation: 'handle_next'
      });

      // Save step 1 data to session manager first
      if (sessionManager) {
        // Save wizard state
        sessionManager.saveState(1, stepData, { completed: true });
        
        // Enhanced KB configuration saving with existing data preservation
        // Requirements: 12.4, 12.5, 12.6 - Maintain existing selections during navigation
        if (stepData.use_knowledge_base && stepData.knowledge_base) {
          const kbConfig = {
            files: (stepData.knowledge_base.files || []).map(file => ({
              ...file,
              // Preserve existing file properties
              selected: file.selected !== undefined ? file.selected : true,
              existing: file.existing || false,
              // Ensure required properties are present
              id: file.id,
              name: file.name,
              size: file.size,
              type: file.type || 'file',
              mime_type: file.mime_type,
              sync_status: file.sync_status || 'synced'
            })),
            urls: stepData.knowledge_base.url ? [{
              id: `url_${Date.now()}`,
              url: stepData.knowledge_base.url,
              status: 'validated',
              validation: { valid: true, accessible: true },
              added_at: new Date().toISOString(),
              existing: true // Mark as existing for editing mode
            }] : [],
            textContent: (stepData.knowledge_base.text_documents || []).map(doc => ({
              ...doc,
              // Preserve existing document properties
              selected: doc.selected !== undefined ? doc.selected : true,
              existing: doc.existing || false
            })),
            metadata: {
              last_updated: new Date().toISOString(),
              total_files: stepData.knowledge_base.files?.length || 0,
              total_urls: stepData.knowledge_base.url ? 1 : 0,
              total_text_items: stepData.knowledge_base.text_documents?.length || 0,
              validation_status: 'valid',
              preserves_existing: true // Flag to indicate existing data preservation
            }
          };
          
          sessionManager.saveKnowledgeBaseConfig(kbConfig);
          
          console.log('Preserved KB configuration during navigation:', {
            filesCount: kbConfig.files.length,
            urlsCount: kbConfig.urls.length,
            textContentCount: kbConfig.textContent.length,
            existingFiles: kbConfig.files.filter(f => f.existing).length,
            existingUrls: kbConfig.urls.filter(u => u.existing).length
          });
        }
      }

      // Save step 1 data to backend wizard session
      const apiPayload = {
        ...stepData,
        parrot_id: isEditing ? id : null,
        session_id: sessionManager?.sessionId // Include session ID if available
      };
      
      console.log('ParrotWizardStep1: Sending to API:', apiPayload);
      
      const wizardResponse = await apiClient.post('/api/parrots/wizard/step1', apiPayload);
      
      if (!wizardResponse.success) {
        throw new Error(wizardResponse.error?.message || 'Failed to save step 1 data');
      }
      
      // Update session manager with backend session ID if we got a new one
      const backendSessionId = wizardResponse.data?.session_id;
      if (sessionManager && backendSessionId && backendSessionId !== sessionManager.sessionId) {
        const oldId = sessionManager.sessionId;
        sessionManager.sessionId = backendSessionId;
        debugLogger.info('Updated session manager with backend session ID', {
          oldSessionId: oldId,
          newSessionId: backendSessionId
        });
      }

      // KB configuration will be queued for sync when user saves at step2 or step3
      // Using Cloudflare Queues for async processing to avoid immediate assignment
      if (sessionManager && stepData.use_knowledge_base && stepData.knowledge_base) {
        debugLogger.info('KB configuration prepared for queued sync', {
          sessionId: sessionManager.sessionId,
          kbFiles: stepData.knowledge_base.files?.length || 0,
          kbUrls: stepData.knowledge_base.url ? 1 : 0,
          syncTiming: 'Will sync on step2/step3 save'
        });
      }
      
      // Add the session_id to the step data for the next step
      const stepDataWithSession = {
        ...stepData,
        session_id: backendSessionId || sessionManager?.sessionId
      };
      
      // Navigate to step 2 with the data including session_id
      const nextRoute = isEditing ? `/parrots/${id}/edit/step2` : '/parrots/new/step2';
      navigate(nextRoute, { 
        state: { 
          step1Data: stepDataWithSession,
          sessionId: backendSessionId || sessionManager?.sessionId,
          isEditing,
          parrotId: id,
          preserveSession: true // Prevent session cleanup on unmount
        } 
      });
      
    } catch (error) {
      console.error('Failed to proceed to step 2:', error);
      
      // Log error with session context
      debugLogger.error('Failed to proceed to step 2', error, {
        component: 'ParrotWizardStep1',
        operation: 'handle_next_error',
        sessionId: sessionManager?.sessionId,
        parrotId: id
      });
      
      showToast('Failed to save progress. Please try again.', 'error');
      setErrors({ general: 'Failed to save progress. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle cancellation
   */
  const handleCancel = () => {
    navigate('/parrots');
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Breadcrumbs */}
          <WizardBreadcrumbs 
            currentStep={1} 
            isEditing={isEditing} 
            parrotId={id}
            className="mb-6" 
          />

          {/* Progress Indicator */}
          <WizardProgressIndicator currentStep={1} />

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {isEditing ? 'Edit Parrot - Step 1' : 'Create New Parrot - Step 1'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
              Configure the basic settings for your AI voice assistant
            </p>
          </div>

          {/* Error Display */}
          {errors.general && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{errors.general}</p>
            </div>
          )}

          {/* Enhanced Loading State with Progress */}
          {(isLoading || initializationState.status !== 'success') && (
            <InitializationProgress
              initializationState={initializationState}
              onRetry={handleRetry}
              onFallback={handleFallback}
              onCancel={() => navigate('/parrots')}
              className="mb-6"
            />
          )}

          {/* Step 1 Component */}
          {initializationState.status === 'success' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 relative">
              <Step1ParrotConfig
                formData={formData}
                onFormDataChange={handleFormDataChange}
                onNext={handleNext}
                onCancel={handleCancel}
                errors={errors}
                isLoading={isLoading}
                sessionManager={sessionManager}
              />
              {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 dark:bg-gray-800 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
                  <div className="flex items-center">
                    <LoadingSpinner size="lg" />
                    <p className="text-gray-600 dark:text-gray-400 ml-4 dark:text-gray-300 dark:text-gray-600">Processing...</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SignedIn>
    </>
  );
}
